import { useEffect, useState, useCallback } from "react"
import { CheckCircle2, X } from "lucide-react"
import { cn } from "@/lib/utils"

interface ProgressToastData {
  id: string
  title: string
  description: string
}

// Global event bus for progress toasts
const listeners = new Set<(toast: ProgressToastData) => void>()

export function showProgressToast(title: string, description: string) {
  const toast: ProgressToastData = {
    id: `${Date.now()}-${Math.random()}`,
    title,
    description,
  }
  listeners.forEach((fn) => fn(toast))
}

export function ProgressToastContainer() {
  const [toasts, setToasts] = useState<ProgressToastData[]>([])

  const addToast = useCallback((toast: ProgressToastData) => {
    setToasts((prev) => [...prev, toast])

    // Auto-dismiss after 5s
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== toast.id))
    }, 5000)
  }, [])

  useEffect(() => {
    listeners.add(addToast)
    return () => {
      listeners.delete(addToast)
    }
  }, [addToast])

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications de progression"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            "flex items-start gap-3 rounded-lg border bg-card px-4 py-3 shadow-lg",
            "animate-in slide-in-from-right-full fade-in-0 duration-300",
            "max-w-sm"
          )}
          role="status"
          data-test="progress-toast"
        >
          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{toast.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{toast.description}</p>
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            className="text-muted-foreground/60 hover:text-foreground transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
